import React, { useEffect, useState } from 'react'
import { useTheme } from '@/context/ThemeContext'
import type { PlanRecord } from '../types/index.js'

interface Props {
  plan: PlanRecord | null
  open: boolean
  onClose: () => void
  onSubmit: (input: { name: string; seats: number; monthlyAiCredits: number }) => Promise<void>
}

const PlanFormModal: React.FC<Props> = ({ plan, open, onClose, onSubmit }) => {
  const { currentTheme: t } = useTheme()
  const [name, setName] = useState('')
  const [seats, setSeats] = useState('')
  const [monthlyAiCredits, setMonthlyAiCredits] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(plan?.name ?? '')
      setSeats(plan ? String(plan.seats) : '')
      setMonthlyAiCredits(plan ? String(plan.monthlyAiCredits) : '')
      setError(null)
    }
  }, [open, plan])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const seatsNum = Number(seats)
    const creditsNum = Number(monthlyAiCredits)
    if (!name.trim() || !Number.isFinite(seatsNum) || seatsNum <= 0 || !Number.isFinite(creditsNum) || creditsNum <= 0) {
      setError('Enter a valid name, seat count, and credit pool.')
      return
    }
    setSaving(true)
    try {
      await onSubmit({ name: name.trim(), seats: seatsNum, monthlyAiCredits: creditsNum })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save plan')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
        onClick={onClose}
      />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[92%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl p-5"
        style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.boxShadow }}
      >
        <h2 className="text-base font-semibold mb-4" style={{ color: t.text }}>
          {plan ? 'Edit plan' : 'New plan'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: t.secondrytext }}>
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Development"
              className="mt-1 w-full h-10 rounded-lg px-3 text-sm outline-none"
              style={{ backgroundColor: t.inputBackground, color: t.text, border: `1px solid ${t.border}` }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: t.secondrytext }}>
              Seats
            </label>
            <input
              type="number"
              min={1}
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              className="mt-1 w-full h-10 rounded-lg px-3 text-sm outline-none"
              style={{ backgroundColor: t.inputBackground, color: t.text, border: `1px solid ${t.border}` }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: t.secondrytext }}>
              Monthly AI credits
            </label>
            <input
              type="number"
              min={1}
              value={monthlyAiCredits}
              onChange={(e) => setMonthlyAiCredits(e.target.value)}
              className="mt-1 w-full h-10 rounded-lg px-3 text-sm outline-none"
              style={{ backgroundColor: t.inputBackground, color: t.text, border: `1px solid ${t.border}` }}
            />
          </div>

          {error && (
            <p className="text-xs rounded-lg px-3 py-2" style={{ backgroundColor: t.errorBackground, color: t.errorText }}>
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
              style={{ backgroundColor: t.accent, color: t.buttonText }}
            >
              {saving ? 'Saving…' : plan ? 'Save changes' : 'Create plan'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default React.memo(PlanFormModal)
